// src/components/AgentPipeline/AgentPipeline.tsx

import React, { useState } from 'react';
import { AgentBreakdown } from '../../services/AlpacaService';
import styles from './AgentPipeline.module.css';

interface AgentDef {
  key: string;
  label: string;
  icon: string;
  phase: number;
}

const PHASE_0_AGENTS: AgentDef[] = [{ key: 'regime_agent', label: 'Regime', icon: '\uD83C\uDF0D', phase: 0 }];

const PHASE_1_AGENTS: AgentDef[] = [
  { key: 'scanner_agent', label: 'Scanner', icon: '\uD83D\uDD0D', phase: 1 },
  { key: 'exit_agent', label: 'Exit', icon: '\uD83D\uDEAA', phase: 1 },
  { key: 'sentiment_agent', label: 'Sentiment', icon: '\uD83D\uDCF0', phase: 1 },
  { key: 'correlation_agent', label: 'Correlation', icon: '\uD83D\uDD17', phase: 1 },
  { key: 'volume_agent', label: 'Volume', icon: '\uD83D\uDCCA', phase: 1 },
  { key: 'trend_agent', label: 'Trend', icon: '\uD83D\uDCC8', phase: 1 },
  { key: 'relative_strength_agent', label: 'Rel Strength', icon: '\u26A1', phase: 1 },
];

const PHASE_SEQ_AGENTS: AgentDef[] = [
  { key: 'analyst_agent', label: 'Analyst', icon: '\uD83E\uDDE0', phase: 2 },
  { key: 'earnings_agent', label: 'Earnings', icon: '\uD83D\uDCC5', phase: 3 },
  { key: 'risk_agent', label: 'Risk', icon: '\uD83D\uDEE1\uFE0F', phase: 4 },
  { key: 'timing_agent', label: 'Timing', icon: '\u23F0', phase: 5 },
];

interface Props {
  breakdown?: AgentBreakdown | null;
  isRunning?: boolean;
  regime?: string | null;
}

const Connector: React.FC = () => (
  <div className={styles.connector}>
    <div className={styles.connectorLine} />
    <span className={styles.connectorArrow}>{'\u25BC'}</span>
  </div>
);

const AgentPipeline: React.FC<Props> = ({ breakdown, isRunning = false, regime }) => {
  const [hoveredAgent, setHoveredAgent] = useState<string | null>(null);

  const getStatus = (key: string) => {
    if (!breakdown) return 'idle';
    const agent = breakdown[key];
    if (!agent) return 'idle';
    if (!agent.called) return 'skipped';
    if (agent.error) return 'error';
    return 'success';
  };

  const totalAgents = breakdown ? Object.values(breakdown).filter((a) => a.called).length : 0;
  const totalTime = breakdown ? Object.values(breakdown).reduce((sum, a) => sum + (a.elapsed_s || 0), 0) : 0;

  const renderChip = (agent: AgentDef, colorClass: string) => {
    const status = getStatus(agent.key);
    const agentData = breakdown?.[agent.key];
    const isHovered = hoveredAgent === agent.key;

    return (
      <div
        key={agent.key}
        className={`${styles.agentChip} ${colorClass} ${styles[`status_${status}`]} ${isRunning && status === 'idle' ? styles.chipRunning : ''}`}
        onMouseEnter={() => setHoveredAgent(agent.key)}
        onMouseLeave={() => setHoveredAgent(null)}
      >
        <span className={styles.chipIcon}>{agent.icon}</span>
        <span className={styles.chipLabel}>{agent.label}</span>
        {agentData?.called && (
          <span className={`${styles.statusBadge} ${agentData.error ? styles.statusBadgeError : styles.statusBadgeOk}`}>
            {agentData.error ? '!' : agentData.results}
          </span>
        )}
        {isHovered && agentData?.called && (
          <div className={styles.tooltip}>
            {agentData.error
              ? `Error: ${agentData.error.slice(0, 30)}`
              : `${agentData.results} results \u00B7 ${agentData.elapsed_s}s`}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>
          <span className={styles.headerIcon}>{'\uD83E\uDDE0'}</span>
          Agent Pipeline
        </span>
        <span className={styles.headerMeta}>
          {breakdown ? `${totalAgents} agents \u00B7 ${totalTime.toFixed(1)}s total` : 'Awaiting first run'}
        </span>
      </div>

      <div className={styles.pipeline}>
        {/* ── Phase 0: Regime Classification ── */}
        <div className={`${styles.phase} ${styles.phaseRegime} ${styles.delay0}`}>
          <div className={styles.phaseHeader}>
            <div className={`${styles.phaseIconWrap} ${styles.phaseIconWrapTeal}`}>{'\uD83C\uDF0D'}</div>
            <div className={styles.phaseInfo}>
              <span className={`${styles.phaseName} ${styles.phaseNameTeal}`}>Phase 0 — Regime Classification</span>
              <span className={styles.phaseDesc}>Determine market regime before analysis begins</span>
            </div>
          </div>
          <div className={styles.agentGrid}>{PHASE_0_AGENTS.map((a) => renderChip(a, styles.chipTeal))}</div>
        </div>

        <Connector />

        {/* ── Phase 1: Parallel Analysis ── */}
        <div className={`${styles.phase} ${styles.phaseParallel} ${styles.delay1}`}>
          <div className={styles.phaseHeader}>
            <div className={`${styles.phaseIconWrap} ${styles.phaseIconWrapAmber}`}>{'\uD83D\uDD0D'}</div>
            <div className={styles.phaseInfo}>
              <span className={`${styles.phaseName} ${styles.phaseNameAmber}`}>Phase 1 — Parallel Analysis</span>
              <span className={styles.phaseDesc}>All 7 agents run simultaneously on the signal</span>
            </div>
            <span className={`${styles.phaseBadge} ${styles.phaseBadgeAmber}`}>7 agents</span>
          </div>
          <div className={styles.agentGrid}>{PHASE_1_AGENTS.map((a) => renderChip(a, styles.chipAmber))}</div>
        </div>

        <Connector />

        {/* ── Phase 2-5: Sequential Pipeline ── */}
        <div className={`${styles.phase} ${styles.phaseSequential} ${styles.delay2}`}>
          <div className={styles.phaseHeader}>
            <div className={`${styles.phaseIconWrap} ${styles.phaseIconWrapIndigo}`}>{'\uD83E\uDDE0'}</div>
            <div className={styles.phaseInfo}>
              <span className={`${styles.phaseName} ${styles.phaseNameIndigo}`}>Phase 2–5 — Sequential Pipeline</span>
              <span className={styles.phaseDesc}>Deep analysis, earnings check, risk assessment, and timing</span>
            </div>
            <span className={`${styles.phaseBadge} ${styles.phaseBadgeIndigo}`}>sequential</span>
          </div>
          <div className={styles.sequentialFlow}>
            {PHASE_SEQ_AGENTS.map((agent, i) => (
              <React.Fragment key={agent.key}>
                {renderChip(agent, styles.chipIndigo)}
                {i < PHASE_SEQ_AGENTS.length - 1 && <span className={styles.seqArrow}>{'\u25B6'}</span>}
              </React.Fragment>
            ))}
          </div>
        </div>

        <Connector />

        {/* ── Claude brain footer (strategist hero style) ── */}
        <div className={styles.brainFooter}>
          <div className={`${styles.brainCard} ${isRunning ? styles.brainPulse : ''}`}>
            <div className={styles.brainIconWrap}>{'\uD83E\uDDE0'}</div>
            <div className={styles.brainInfo}>
              <span className={styles.brainLabel}>Claude Strategist</span>
              <span className={styles.brainSub}>Final decision engine</span>
            </div>
            {regime && <span className={styles.regimeTag}>{regime}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentPipeline;
